import type { DocType, EmploymentType, LeaveType, WorkMode } from "@/api/types";

/** Policy names, spelled the way the Leave & Holiday Policy spells them. */
export const LEAVE_TYPE_LABEL: Record<LeaveType, string> = {
  EL: "Earned Leave",
  CL: "Casual Leave",
  SL: "Sick Leave",
  Menstrual: "Menstrual Leave",
  Special: "Special Leave",
  OptionalHoliday: "Optional Holiday",
  CompOff: "Comp Off",
  LWP: "Leave Without Pay",
};

export const LEAVE_TYPE_SHORT: Record<LeaveType, string> = {
  EL: "EL",
  CL: "CL",
  SL: "SL",
  Menstrual: "Menstrual",
  Special: "Special",
  OptionalHoliday: "Optional",
  CompOff: "Comp Off",
  LWP: "LWP",
};

export function leaveLabel(type: string): string {
  return LEAVE_TYPE_LABEL[type as LeaveType] ?? type;
}

// The monthly summary's trailing column isn't a real applied-for LeaveType —
// it's `computeLwpDays`' inference of an unexplained absence (no attendance
// record, no approved leave of ANY type covering the day, LWP included). Now
// that "LWP" is also a real leave type someone can apply for (see
// LEAVE_TYPE_LABEL.LWP above), this needed a distinct label so the two don't
// both read as "LWP" side by side in the same table.
export const UNRECORDED_ABSENCE_LABEL = "Unrecorded";

export const DOC_TYPE_LABEL: Record<DocType, string> = {
  aadhaar: "Aadhaar",
  pan: "PAN",
  cancelled_cheque: "Cancelled cheque",
  offer_letter: "Offer letter",
  compensation_sheet: "Compensation sheet",
};

/** Only these three can be uploaded by the employee themselves. */
export const SELF_UPLOADABLE: DocType[] = ["aadhaar", "pan", "cancelled_cheque"];
export const ALL_DOC_TYPES: DocType[] = [
  "aadhaar",
  "pan",
  "cancelled_cheque",
  "offer_letter",
  "compensation_sheet",
];

export const WORK_MODE_LABEL: Record<WorkMode, string> = {
  onsite: "Onsite",
  hybrid: "Hybrid",
  remote: "Remote",
};

export const EMPLOYMENT_TYPE_LABEL: Record<EmploymentType, string> = {
  FTE: "Full-time",
  Intern: "Intern",
};

/** Onboarding returns raw column names; these are what a person should read. */
export const PROFILE_FIELD_LABEL: Record<string, string> = {
  account_number: "Bank account number",
  account_holder_name: "Account holder name",
  bank_name: "Bank name",
  ifsc_code: "IFSC code",
  current_address: "Current address",
  permanent_address: "Permanent address",
  gender: "Gender",
  aadhaar_number: "Aadhaar number",
  pan_number: "PAN number",
  emergency_contact_1_name: "Emergency contact 1 name",
  emergency_contact_1_number: "Emergency contact 1 number",
  emergency_contact_2_name: "Emergency contact 2 name",
  emergency_contact_2_number: "Emergency contact 2 number",
};

export function profileFieldLabel(field: string): string {
  return PROFILE_FIELD_LABEL[field] ?? field;
}
